import { useMutation } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { failureFromThrown } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { safeNextPath } from '../lib/next-path.ts'
import type { SessionEndReason } from '../lib/session.ts'
import { useSession, useSessionStore } from '../session/context.tsx'
import { BrandMark } from '../shell/brand-mark.tsx'
import { PRODUCT_NAME, useDocumentTitle } from '../shell/document-title.ts'
import { EnvironmentBadge } from '../shell/environment-badge.tsx'
import { Notice } from '../shell/notice.tsx'

/**
 * Sign in — docs/ux/PLATFORM-ADMIN.md §4.2, and §4.3, which is the same screen seen by
 * somebody who should not be here.
 *
 * One page, one form: nickname, password, one submit. No tenant picker, no "remember me",
 * no social sign-in, no sign-up link, and **no password reset** — there is no email in
 * this system, and a reset a stranger could trigger against the platform owner's account
 * would be a hole with no upside. §4.5 says what a genuinely locked-out owner does
 * instead, and it is an operations path rather than a screen.
 *
 * Nothing here is branded, and nothing here names a tenant. A tenant's user who finds
 * this hostname sees exactly this page: their credentials are not accepted, the message
 * is the same one a wrong password gets, and **the screen never says that an account
 * exists elsewhere**.
 */

/** The line shown when the previous session ended by itself (§4.4, §18.2). */
const END_REASON_LINES: Partial<Record<SessionEndReason, string>> = {
  idle: 'You were signed out after thirty minutes without any activity.',
  expired: 'Your session ended. Sign in again.',
  // A deliberate sign-out needs no explanation: the owner just pressed the button.
}

/**
 * Where the nickname field takes focus on its own: a window at least as wide as the stylesheet's
 * phone breakpoint (`styles.css`, 40 rem), in `rem` so that a larger text size moves it with the CSS.
 *
 * Below it the field is left alone, because focusing it raises the on-screen keyboard over the
 * page — and over the environment word the owner has not yet read (§5.1), on the page whose job is
 * to say which estate they are about to enter. Tokens never persist (§4.4), so on a laptop this is
 * every session, and the first keystroke used to go nowhere (TAS-13, S2).
 */
const AUTOFOCUS_QUERY = '(min-width: 40rem)'

/** True where there is room for a keyboard to be a non-event; also where the question cannot be asked. */
function focusesOnLoad(): boolean {
  return typeof window.matchMedia !== 'function' || window.matchMedia(AUTOFOCUS_QUERY).matches
}

export type SignInScreenProps = {
  /** Where to go after signing in — the route that was asked for while signed out. */
  readonly next?: string
}

export function SignInScreen({ next }: SignInScreenProps) {
  useDocumentTitle('Sign in')
  const store = useSessionStore()
  const session = useSession()
  const router = useRouter()
  const nicknameId = useId()
  const passwordId = useId()
  const errorId = useId()
  const nicknameField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (focusesOnLoad()) {
      nicknameField.current?.focus()
    }
  }, [])

  const [nickname, setNickname] = useState('')
  const [password, setPassword] = useState('')
  const [failureCode, setFailureCode] = useState<string | undefined>(undefined)
  /**
   * `rate_limited` leaves the submit inert until an input changes (§4.2). It is the one
   * state on this form that outlives the request that produced it.
   */
  const [rateLimited, setRateLimited] = useState(false)

  const endedLine =
    session.endedReason === undefined ? undefined : END_REASON_LINES[session.endedReason]

  const signIn = useMutation({
    mutationFn: (credentials: { nickname: string; password: string }) => store.signIn(credentials),
    onSuccess: () => {
      setPassword('')
      // Checked again here, not only where the URL was parsed: this screen is the one
      // that performs the navigation, and an open redirect is not a bug worth having
      // twice the distance from its guard.
      // `history.push`, not `navigate({ to })`: `to` is a path pattern, and a remembered address
      // now carries its query string (`/tenants?q=acme&page=2`), which `to` would take for part of
      // the path. Pushing the address whole puts the router exactly where the link pointed.
      router.history.push(safeNextPath(next) ?? '/tenants')
    },
    onError: (thrown: unknown) => {
      // The submitted password is never echoed, never put in a message, never written to
      // the console, and never sent anywhere but the sign-in call — **HB4** is a system
      // that did all three. Only the code survives this branch.
      const failure = failureFromThrown(thrown)
      setFailureCode(failure.code)
      setRateLimited(failure.code === 'rate_limited')
    },
  })

  const changed = (apply: () => void) => {
    apply()
    // Any edit clears the previous answer, and re-enables a submit that rate limiting
    // had made inert.
    setFailureCode(undefined)
    setRateLimited(false)
    store.clearEndReason()
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    store.clearEndReason()

    if (nickname.trim() === '' || password === '') {
      // A failure that never reaches the network (§20.3). Sending it would tell the API
      // nothing it could answer better.
      setFailureCode('validation_failed')
      return
    }

    signIn.mutate({ nickname, password })
  }

  const message = failureCode === undefined ? undefined : messageFor('signIn', failureCode)

  return (
    <main className="signin">
      <div className="signin-column">
        <div className="signin-brand">
          {/*
            The product mark and name, and nothing under them but the estate. The name already
            ends in "Admin", so a tagline here would be the same sentence said twice — and the
            line that does earn its place is the one below, which says which estate this is.
          */}
          <BrandMark size={52} />
          <h1>{PRODUCT_NAME}</h1>
          {/*
            Which estate this is, before anything is typed. There is no masthead on this page
            and §5.1 wants the word stated always — and "am I about to sign into prod" is a
            question worth answering before the password, not after. The badge draws the rail
            along the top edge here too.
          */}
          <div className="signin-estate">
            <span>You are signing in to</span>
            <EnvironmentBadge />
          </div>
        </div>

        <div className="signin-card">
          {endedLine === undefined ? undefined : <Notice tone="warning">{endedLine}</Notice>}
          {message === undefined ? undefined : (
            <Notice tone="failure" assertive id={errorId}>
              {message}
            </Notice>
          )}

          <form className="form" onSubmit={submit}>
            <div className="field">
              <label htmlFor={nicknameId}>Nickname</label>
              {/*
                A nickname is typed exactly as it was chosen, and the failure message is deliberately
                the same for every cause (§4.2) — so a phone keyboard that capitalises the first
                letter, or "corrects" a nickname into a word, fails the sign-in without a clue as to
                why (TAS-13, S1).
              */}
              <input
                ref={nicknameField}
                id={nicknameId}
                name="nickname"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                value={nickname}
                // The fields stay usable while the request is in flight; only the submit is
                // inert (§4.2).
                onChange={(event) => {
                  changed(() => setNickname(event.target.value))
                }}
                {...(message === undefined ? {} : { 'aria-describedby': errorId })}
              />
            </div>

            <div className="field">
              <label htmlFor={passwordId}>Password</label>
              {/* `type="password"` with the browser's own reveal control, and nothing else. */}
              <input
                id={passwordId}
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => {
                  changed(() => setPassword(event.target.value))
                }}
                {...(message === undefined ? {} : { 'aria-describedby': errorId })}
              />
            </div>

            <button
              type="submit"
              className="button button-primary button-block"
              disabled={signIn.isPending || rateLimited}
              // The pending state is told to assistive technology and drawn from the same
              // attribute, so the label and the spinner can never disagree.
              aria-busy={signIn.isPending}
            >
              {signIn.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
        <p className="signin-note">
          Platform administrators only. The session lives in this tab: a reload or thirty idle
          minutes signs you out.
        </p>
      </div>
    </main>
  )
}
